import { useIsMutating } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { parseError } from '@/lib/errors'
import {
  POLICY_CHANGE_WRITE_KEY,
  PolicyChangeAction,
  type PolicyChangeRequest,
  PolicyChangeStatus,
  policyChangeWrites,
  usePolicyChangeMutation,
} from '@/models/policyChange'

/** Lets the customer call a request off before it is quoted; nothing changes and they can reopen it later. */
export function WithdrawRequest({
  orgId,
  request,
}: {
  orgId: string | undefined
  request: PolicyChangeRequest
}) {
  const navigate = useNavigate()
  const cancel = usePolicyChangeMutation(orgId, policyChangeWrites.cancel)
  // A save still in flight would land after the withdrawal and put the editable request back.
  const writing = useIsMutating({ mutationKey: POLICY_CHANGE_WRITE_KEY }) > 0
  if (!request.actions.includes(PolicyChangeAction.CANCEL)) return null
  const draft = request.status === PolicyChangeStatus.DRAFT

  return (
    <div className="flex items-center justify-end gap-3 text-sm text-neutral-600">
      Changed your mind?
      <ConfirmationDialog
        title={draft ? 'Cancel this request?' : 'Withdraw this request?'}
        description={
          draft
            ? 'Nothing changes on your policies. You can reopen it later.'
            : 'Nothing changes on your policies, and your insurance broker stops working on it. You can reopen it later.'
        }
        confirmLabel={draft ? 'Cancel request' : 'Withdraw'}
        cancelLabel="Keep it"
        onConfirm={() =>
          cancel.mutateAsync(request.id).then(
            // The request page, not the form a new request was started in, shows it canceled.
            () => void navigate(`/insure/policy-changes/${request.id}`, { replace: true }),
            (error: unknown) => {
              toast.error(parseError(error))
            }
          )
        }
      >
        <Button type="button" variant="outline" size="sm" disabled={writing}>
          {draft ? 'Cancel request' : 'Withdraw request'}
        </Button>
      </ConfirmationDialog>
    </div>
  )
}
